import { useCallback, useEffect, useRef, useState } from 'react';

import {
  isAbortError,
  parseWarehouseError,
  WarehouseApiError,
} from '../api/warehouse.api';

export interface LoadState<T> {
  data: T | null;
  loading: boolean;
  error: WarehouseApiError | null;
  reload: () => void;
  /**
   * Put data the caller already holds (e.g. the entity a mutation returned).
   * Any fetch still in flight is aborted so its older answer can't land on
   * top of this one.
   */
  setData: (next: T | null) => void;
}

/**
 * Fetch-on-deps for the WMS screens. The fetcher receives an AbortSignal —
 * pass it to the `warehouseApi` call — which is aborted when the deps change,
 * on `reload()`, on `setData()` and on unmount, so a stale response never
 * overwrites a newer one (React StrictMode's double mount included; the API
 * layer de-duplicates identical in-flight GETs). `enabled=false` skips the
 * request (e.g. no hub picked yet) and clears the data.
 */
export function useLoad<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: unknown[],
  enabled = true
): LoadState<T> {
  const [data, setDataState] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<WarehouseApiError | null>(null);
  const [nonce, setNonce] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const inflightRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!enabled) {
      setDataState(null);
      setLoading(false);
      setError(null);
      return;
    }
    const controller = new AbortController();
    inflightRef.current = controller;
    setLoading(true);
    setError(null);
    fetcherRef
      .current(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setDataState(res);
      })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) return;
        setError(parseWarehouseError(err));
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
        if (inflightRef.current === controller) inflightRef.current = null;
      });
    return () => {
      controller.abort();
      if (inflightRef.current === controller) inflightRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, nonce, ...deps]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  const setData = useCallback((next: T | null) => {
    if (inflightRef.current) {
      inflightRef.current.abort();
      inflightRef.current = null;
      setLoading(false);
    }
    setError(null);
    setDataState(next);
  }, []);

  return { data, loading, error, reload, setData };
}
